import React, { useState } from "react";
import MarketingLayout from "@/components/layout/MarketingLayout";
import api from "@/lib/api";
import { toast } from "sonner";
import { Check, ArrowUpRight, Calendar, MapPin, Mail, Phone, Sparkles } from "lucide-react";

export default function Booking() {
  const [form, setForm] = useState({
    client_name: "",
    partner_name: "",
    client_email: "",
    contact_type: "whatsapp",
    client_phone: "",
    wedding_date: "",
    event_location: "",
    region: "pakistan",
    functions: [],
    experience_type: "Legacy Wedding",
    event_days: "",
    guest_scale: "",
    budget: "",
    notes: ""
  });

  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  // Get current date dynamically in YYYY-MM-DD format to prevent past date selection
  const today = new Date().toISOString().split("T")[0];

  const functionOptions = [
    "Mayun / Dholki",
    "Mehndi",
    "Nikah",
    "Baraat",
    "Walima / Reception",
    "Portrait Session / Shoot"
  ];

  const toggleFunction = (fn) => {
    setForm(prev => ({
      ...prev,
      functions: prev.functions.includes(fn)
        ? prev.functions.filter(f => f !== fn)
        : [...prev.functions, fn]
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      // Clean budget string before converting to number
      const numericBudget = form.budget ? Number(form.budget.replace(/[^0-9.]/g, '')) : null;

      await api.post("/bookings", {
        client_name: `${form.client_name} ${form.partner_name ? `& ${form.partner_name}` : ""}`.trim(),
        client_email: form.client_email,
        client_phone: `${form.contact_type.toUpperCase()}: ${form.client_phone}`,
        region: form.region,
        event_type: form.functions.join(", ") || "Wedding Coverage",
        event_date: form.wedding_date || new Date().toISOString(),
        event_location: form.event_location,
        budget: numericBudget,
        notes: `Experience: ${form.experience_type} | Days: ${form.event_days} | Guests: ${form.guest_scale} | Notes: ${form.notes}`
      });

      setDone(true);
      toast.success("Enquiry submitted successfully!");
    } catch (error) {
      console.error("Booking submission error:", error);
      if (error?.code === 'ERR_NETWORK' || error?.message?.includes('Network Error')) {
        toast.error("Backend server is not running. Please start the server and try again.");
      } else {
        toast.error("Failed to submit enquiry. Please try again later.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <MarketingLayout>
      {/* Cinematic Hero Header */}
      <section className="relative pt-32 pb-20 md:pt-44 md:pb-32 bg-[#121A12] text-[#F1F2EC] overflow-hidden" data-testid="booking-hero">
        <div 
          className="absolute inset-0 opacity-40 mix-blend-luminosity bg-cover bg-center scale-105 transition-transform duration-1000 ease-out" 
          style={{ backgroundImage: `url('https://images.unsplash.com/photo-1519741497674-611481863552?q=80&w=2000&auto=format&fit=crop')` }} 
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#121A12] via-[#121A12]/70 to-transparent" />
        
        <div className="container-x relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="overline text-[#C9D1B5] mb-4 tracking-[0.3em] flex items-center gap-3 text-xs uppercase font-mono">
            <span className="w-8 h-[1px] bg-[#C9D1B5]" /> Get in Touch
          </div>
          <h1 className="font-serif text-4xl sm:text-6xl md:text-7xl lg:text-8xl font-normal tracking-tight max-w-4xl text-white leading-[1.1]">
            Let's begin <span className="italic font-serif text-[#C9D1B5]">your story.</span>
          </h1>
          <div className="mt-8 inline-flex items-center gap-3 px-5 py-2.5 bg-white/5 backdrop-blur-md border border-white/10 text-xs font-mono tracking-[0.2em] uppercase text-[#C9D1B5] rounded-full shadow-lg">
            <Sparkles size={14} className="text-[#C9D1B5] animate-pulse" />
            <span>2026 Season</span> · <span className="text-white">Now Accepting Enquiries</span>
          </div>
        </div>
      </section>

      {/* Main Questionnaire Section */}
      <section className="py-16 md:py-24 bg-background text-foreground" data-testid="booking-content">
        <div className="container-x max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {done ? (
            <div className="max-w-2xl mx-auto border border-border/80 bg-card/50 backdrop-blur-sm p-8 sm:p-14 text-center rounded-sm shadow-xl transition-all" data-testid="booking-success">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6 text-primary">
                <Check size={32} strokeWidth={1.5} />
              </div>
              <h2 className="font-serif text-3xl sm:text-4xl">Conversation Initiated</h2>
              <p className="text-muted-foreground mt-3 text-sm sm:text-base leading-relaxed">
                Every enquiry is read personally by Dobhai Official. We'll reply within 24 hours.
              </p>
              <button 
                onClick={() => setDone(false)} 
                className="mt-8 px-8 py-3.5 bg-primary text-primary-foreground font-mono text-xs uppercase tracking-[0.2em] transition-all hover:bg-primary/90 hover:shadow-lg active:scale-95"
              >
                Submit another enquiry
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
              
              {/* Left Column: Philosophy & Direct Info */}
              <div className="lg:col-span-5 lg:sticky lg:top-28 space-y-10">
                <div>
                  <span className="text-[11px] uppercase font-mono tracking-[0.3em] text-primary mb-3 block font-semibold">
                    Approach & Philosophy
                  </span>
                  <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-normal tracking-tight leading-tight">
                    This is a conversation, <span className="italic text-primary">not a transaction.</span>
                  </h2>
                  <p className="text-muted-foreground mt-6 leading-relaxed text-sm sm:text-base">
                    Every enquiry is read personally by the Dobhai Official team. We don’t use templates. You'll receive a genuine, thoughtful response that addresses your specific wedding vision.
                  </p>
                </div>

                <div className="space-y-6 pt-6 border-t border-border/60">
                  <div className="flex items-start gap-4">
                    <div className="p-2 bg-secondary text-secondary-foreground rounded-sm">
                      <Calendar size={18} />
                    </div>
                    <div>
                      <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-muted-foreground">Response Time</div>
                      <div className="font-serif text-lg sm:text-xl mt-0.5">Within 24 hours</div>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="p-2 bg-secondary text-secondary-foreground rounded-sm">
                      <Sparkles size={18} />
                    </div>
                    <div>
                      <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-muted-foreground">Legacy Weddings / Year</div>
                      <div className="font-serif text-lg sm:text-xl mt-0.5">Exclusive dates are limited</div>
                    </div>
                  </div>
                </div>

                <div className="space-y-4 pt-6 border-t border-border/60">
                  <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-muted-foreground">Direct Contacts & Desks</div>
                  <div className="flex flex-col gap-3">
                    <a href="mailto:usmansajid25@gmail.com" className="font-serif text-lg hover:text-primary transition-colors inline-flex items-center gap-2 group w-fit">
                      <Mail size={16} className="text-muted-foreground group-hover:text-primary transition-colors" />
                      usmansajid25@gmail.com 
                      <ArrowUpRight size={16} className="opacity-0 -translate-y-1 translate-x-1 group-hover:opacity-100 group-hover:translate-y-0 group-hover:translate-x-0 transition-all" />
                    </a>
                    <div className="text-xs font-mono text-muted-foreground space-y-2 pt-1">
                      <div className="flex items-center gap-2">
                        <Phone size={14} /> Pakistan Desk: +92 323 5464594
                      </div>
                      <div className="flex items-center gap-2">
                        <Phone size={14} /> International Desk: +974 5053 8523
                      </div>
                    </div>
                  </div>
                </div>

                <div className="p-4 bg-muted/40 border border-border/40 text-xs text-muted-foreground leading-relaxed font-mono rounded-sm">
                  For international clients: we are experienced with diaspora weddings and work seamlessly across time zones.
                </div>
              </div>

              {/* Right Column: Interactive Form */}
              <div className="lg:col-span-7 bg-card border border-border p-6 sm:p-10 md:p-12 rounded-sm shadow-xl">
                <form onSubmit={submit} className="space-y-8" data-testid="booking-form">
                  <div className="border-b border-border/60 pb-6">
                    <h3 className="font-serif text-2xl sm:text-3xl font-normal">Tell us about your wedding</h3>
                    <p className="text-xs text-muted-foreground mt-2 leading-relaxed font-mono">
                      These questions help us guide you quickly and accurately.
                    </p>
                  </div>

                  {/* Names Row */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div className="group">
                      <label className="block text-[10px] uppercase font-mono tracking-[0.2em] text-muted-foreground mb-2 group-focus-within:text-primary transition-colors">
                        Your Name *
                      </label>
                      <input
                        required
                        type="text"
                        placeholder="First & last name"
                        value={form.client_name}
                        onChange={(e) => setForm({ ...form, client_name: e.target.value })}
                        className="w-full border-b border-border bg-transparent py-2.5 text-sm focus:outline-none focus:border-primary transition-colors placeholder:text-muted-foreground/40"
                        data-testid="book-name"
                      />
                    </div>
                    <div className="group">
                      <label className="block text-[10px] uppercase font-mono tracking-[0.2em] text-muted-foreground mb-2 group-focus-within:text-primary transition-colors">
                        Partner's Name *
                      </label>
                      <input
                        required
                        type="text"
                        placeholder="First & last name"
                        value={form.partner_name}
                        onChange={(e) => setForm({ ...form, partner_name: e.target.value })}
                        className="w-full border-b border-border bg-transparent py-2.5 text-sm focus:outline-none focus:border-primary transition-colors placeholder:text-muted-foreground/40"
                        data-testid="book-partner"
                      />
                    </div>
                  </div>

                  {/* Contact Row */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div className="group">
                      <label className="block text-[10px] uppercase font-mono tracking-[0.2em] text-muted-foreground mb-2 group-focus-within:text-primary transition-colors">
                        Your Email *
                      </label>
                      <input
                        required
                        type="email"
                        placeholder="yourname@email.com"
                        value={form.client_email}
                        onChange={(e) => setForm({ ...form, client_email: e.target.value })}
                        className="w-full border-b border-border bg-transparent py-2.5 text-sm focus:outline-none focus:border-primary transition-colors placeholder:text-muted-foreground/40"
                        data-testid="book-email"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="group">
                        <label className="block text-[10px] uppercase font-mono tracking-[0.2em] text-muted-foreground mb-2 group-focus-within:text-primary transition-colors">
                          Contact Type *
                        </label>
                        <select
                          value={form.contact_type}
                          onChange={(e) => setForm({ ...form, contact_type: e.target.value })}
                          className="w-full border-b border-border bg-transparent py-2.5 text-sm focus:outline-none focus:border-primary transition-colors cursor-pointer"
                        >
                          <option value="whatsapp">WhatsApp</option>
                          <option value="phone">Phone Number</option>
                          <option value="instagram">Instagram ID</option>
                        </select>
                      </div>
                      <div className="group">
                        <label className="block text-[10px] uppercase font-mono tracking-[0.2em] text-muted-foreground mb-2 group-focus-within:text-primary transition-colors">
                          Details *
                        </label>
                        <input
                          required
                          type="text"
                          placeholder={form.contact_type === "instagram" ? "@yourhandle" : "+92..."}
                          value={form.client_phone}
                          onChange={(e) => setForm({ ...form, client_phone: e.target.value })}
                          className="w-full border-b border-border bg-transparent py-2.5 text-sm focus:outline-none focus:border-primary transition-colors placeholder:text-muted-foreground/40"
                          data-testid="book-phone"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Date & Venue Row */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div className="group">
                      <label className="block text-[10px] uppercase font-mono tracking-[0.2em] text-muted-foreground mb-2 group-focus-within:text-primary transition-colors">
                        Wedding Date *
                      </label>
                      <input
                        required
                        type="date"
                        min={today}
                        value={form.wedding_date}
                        onChange={(e) => setForm({ ...form, wedding_date: e.target.value })}
                        className="w-full border-b border-border bg-transparent py-2.5 text-sm focus:outline-none focus:border-primary transition-colors text-foreground cursor-pointer"
                        data-testid="book-date"
                      />
                    </div>
                    <div className="group">
                      <label className="block text-[10px] uppercase font-mono tracking-[0.2em] text-muted-foreground mb-2 group-focus-within:text-primary transition-colors">
                        City / Venue *
                      </label>
                      <input
                        required
                        type="text"
                        placeholder="e.g. Lahore, DHA Phase 5"
                        value={form.event_location}
                        onChange={(e) => setForm({ ...form, event_location: e.target.value })}
                        className="w-full border-b border-border bg-transparent py-2.5 text-sm focus:outline-none focus:border-primary transition-colors placeholder:text-muted-foreground/40"
                        data-testid="book-location"
                      />
                    </div>
                  </div>

                  {/* Region Select */}
                  <div className="group">
                    <label className="block text-[10px] uppercase font-mono tracking-[0.2em] text-muted-foreground mb-2 group-focus-within:text-primary transition-colors">
                      Where are you based? *
                    </label>
                    <select
                      value={form.region}
                      onChange={(e) => setForm({ ...form, region: e.target.value })}
                      className="w-full border-b border-border bg-transparent py-2.5 text-sm focus:outline-none focus:border-primary transition-colors cursor-pointer"
                      data-testid="book-region"
                    >
                      <option value="pakistan">Pakistan</option>
                      <option value="international">International / Diaspora</option>
                    </select>
                  </div>

                  {/* Functions Selection */}
                  <div>
                    <label className="block text-[10px] uppercase font-mono tracking-[0.2em] text-muted-foreground mb-3">
                      Which functions do you need covered?
                    </label>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                      {functionOptions.map(fn => {
                        const checked = form.functions.includes(fn);
                        return (
                          <button
                            type="button"
                            key={fn}
                            onClick={() => toggleFunction(fn)}
                            className={`flex items-center gap-3 p-3 text-left border transition-all duration-200 text-xs font-mono tracking-wider ${
                              checked 
                                ? "border-primary bg-primary/10 text-primary font-semibold shadow-sm" 
                                : "border-border/80 hover:border-border text-muted-foreground hover:bg-muted/30"
                            }`}
                          >
                            <span className={`w-4 h-4 border flex items-center justify-center transition-colors ${checked ? "border-primary bg-primary text-primary-foreground" : "border-border"}`}>
                              {checked && <Check size={12} strokeWidth={3} />}
                            </span>
                            {fn}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Experience Select */}
                  <div className="group">
                    <label className="block text-[10px] uppercase font-mono tracking-[0.2em] text-muted-foreground mb-2 group-focus-within:text-primary transition-colors">
                      Which experience are you enquiring about? *
                    </label>
                    <select
                      value={form.experience_type}
                      onChange={(e) => setForm({ ...form, experience_type: e.target.value })}
                      className="w-full border-b border-border bg-transparent py-2.5 text-sm focus:outline-none focus:border-primary transition-colors cursor-pointer"
                      data-testid="book-experience"
                    >
                      <option value="Legacy Wedding">Legacy Wedding</option>
                      <option value="Fusion Experience">Fusion Experience</option>
                      <option value="Studio Coverage">Studio Coverage</option>
                      <option value="Portrait Session">Portrait Session / Shoot</option>
                    </select>
                  </div>

                  {/* Event Days & Guest Scale */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div className="group">
                      <label className="block text-[10px] uppercase font-mono tracking-[0.2em] text-muted-foreground mb-2 group-focus-within:text-primary transition-colors">
                        How many event days do you need?
                      </label>
                      <input
                        type="text"
                        placeholder="e.g. 3 days"
                        value={form.event_days}
                        onChange={(e) => setForm({ ...form, event_days: e.target.value })}
                        className="w-full border-b border-border bg-transparent py-2.5 text-sm focus:outline-none focus:border-primary transition-colors placeholder:text-muted-foreground/40"
                        data-testid="book-days"
                      />
                    </div>
                    <div className="group">
                      <label className="block text-[10px] uppercase font-mono tracking-[0.2em] text-muted-foreground mb-2 group-focus-within:text-primary transition-colors">
                        Estimated Guest Scale
                      </label>
                      <input
                        type="text"
                        placeholder="e.g. 300 - 500 guests"
                        value={form.guest_scale}
                        onChange={(e) => setForm({ ...form, guest_scale: e.target.value })}
                        className="w-full border-b border-border bg-transparent py-2.5 text-sm focus:outline-none focus:border-primary transition-colors placeholder:text-muted-foreground/40"
                        data-testid="book-guests"
                      />
                    </div>
                  </div>

                  {/* Budget */}
                  <div className="group">
                    <label className="block text-[10px] uppercase font-mono tracking-[0.2em] text-muted-foreground mb-2 group-focus-within:text-primary transition-colors">
                      Approximate Budget (PKR / USD)
                    </label>
                    <input
                      type="text"
                      placeholder="e.g. 500,000 PKR"
                      value={form.budget}
                      onChange={(e) => setForm({ ...form, budget: e.target.value })}
                      className="w-full border-b border-border bg-transparent py-2.5 text-sm focus:outline-none focus:border-primary transition-colors placeholder:text-muted-foreground/40"
                      data-testid="book-budget"
                    />
                  </div>

                  {/* Vision Notes */}
                  <div className="group">
                    <label className="block text-[10px] uppercase font-mono tracking-[0.2em] text-muted-foreground mb-2 group-focus-within:text-primary transition-colors">
                      Additional Vision / Notes
                    </label>
                    <textarea
                      rows={3}
                      placeholder="Tell us about your story, aesthetic preferences, or specific requests..."
                      value={form.notes}
                      onChange={(e) => setForm({ ...form, notes: e.target.value })}
                      className="w-full border-b border-border bg-transparent py-2.5 text-sm focus:outline-none focus:border-primary transition-colors resize-none placeholder:text-muted-foreground/40"
                      data-testid="book-notes"
                    />
                  </div>

                  {/* Submit Button */}
                  <div className="pt-4">
                    <button
                      type="submit"
                      disabled={submitting}
                      className="w-full h-14 bg-primary text-primary-foreground font-mono text-xs uppercase tracking-[0.25em] flex items-center justify-center gap-3 disabled:opacity-60 transition-all hover:bg-primary/90 hover:shadow-lg active:scale-[0.99] rounded-sm"
                      data-testid="book-submit"
                    >
                      {submitting ? "Initiating Conversation…" : "Send Wedding Enquiry"}
                    </button>
                  </div>
                </form>
              </div>

            </div>
          )}
        </div>
      </section>
    </MarketingLayout>
  );
}